<ng-container *ngIf="state$ | async as state">

    <h2 mat-dialog-title class="pv-dialog-title">
        <mat-icon class="material-symbols-outlined">bar_chart</mat-icon>
        <span class="pv-dialog-title-text">Edit Chart Component</span>
        <button mat-icon-button class="pv-dialog-close" (click)="cancel()" matTooltip="Close" aria-label="Close dialog">
            <mat-icon>close</mat-icon>
        </button>
    </h2>

    <!-- LOADING: white card loader while measurements load -->
    <div class="pv-dialog-loading" *ngIf="state.status === Status.LOADING; else readyTpl">
        <pv-spinner message="Loading options…" fill="white"></pv-spinner>
    </div>

    <ng-template #readyTpl>
        <mat-dialog-content class="pv-dialog-body">
            <form id="report_chart_component_form"
                [formGroup]="formGroup"
                (ngSubmit)="attempt()"
                autocomplete="off"
            >

                <div class="field-block">
                    <label class="field-label">Chart label</label>
                    <mat-form-field appearance="outline" class="full-width">
                        <input matInput type="text" formControlName="label" autocomplete="off">
                        <mat-error>Label max 40 chars</mat-error>
                    </mat-form-field>
                </div>

                <div class="field-block">
                    <label class="field-label">Chart type</label>
                    <div class="chip-row">
                        <button type="button" *ngFor="let chartType of chartTypeOptions"
                                class="opt-chip"
                                [class.active]="typeControl.value === chartType.value"
                                [attr.aria-pressed]="typeControl.value === chartType.value"
                                [matTooltip]="chartType.label"
                                (click)="setFormValue(typeControl, chartType.value); $event.preventDefault();">
                            <mat-icon class="material-symbols-outlined">{{ chartType.icon }}</mat-icon>
                            <span>{{ chartType.short }}</span>
                        </button>
                    </div>
                </div>

                <div class="two-col">
                    <div class="field-block">
                        <label class="field-label">Primary measurement</label>
                        <mat-form-field appearance="outline" class="full-width">
                            <mat-select formControlName="measure" placeholder="Select measurement…">
                                <mat-option>
                                    <ngx-mat-select-search
                                        [formControl]="measureGroupsFilterCtrl"
                                        placeholderLabel="Find Measurement..."
                                        noEntriesFoundLabel="No matching measurement found">
                                    </ngx-mat-select-search>
                                </mat-option>
                                <mat-optgroup *ngFor="let group of filteredMeasureGroups | async; trackBy: trackById" [label]="group.category.label">
                                    <mat-option
                                        *ngFor="let meas of group.data; trackBy: trackById"
                                        [value]="meas.id"
                                        (click)="$event.stopPropagation()">
                                        {{meas.label }} [{{meas.unit}}]
                                    </mat-option>
                                </mat-optgroup>
                            </mat-select>
                            <mat-error>Select primary measurement</mat-error>
                        </mat-form-field>
                    </div>

                    <div class="field-block">
                        <label class="field-label">Chart context</label>
                        <mat-form-field appearance="outline" class="full-width">
                            <mat-select formControlName="context" required placeholder="Select context…">
                                <mat-option *ngFor="let option of chartContextOptions" [value]="option.value">{{ option.label }}</mat-option>
                            </mat-select>
                            <mat-error>Select chart context</mat-error>
                        </mat-form-field>
                    </div>
                </div>

                <ng-container [ngSwitch]="typeControl.value">
                    <!--* SCATTER -->
                    <ng-container *ngSwitchCase="'scatter'">
                        <div class="field-block">
                            <label class="field-label">Dependent measurement</label>
                            <mat-form-field appearance="outline" class="full-width">
                                <mat-select formControlName="secondaryMeasure" placeholder="Select measurement…">
                                    <mat-option>
                                        <ngx-mat-select-search
                                            [formControl]="secondaryMeasureGroupsFilterCtrl"
                                            placeholderLabel="Find Measurement..."
                                            noEntriesFoundLabel="No matching measurement found">
                                        </ngx-mat-select-search>
                                    </mat-option>
                                    <mat-optgroup *ngFor="let group of filteredSecondaryMeasureGroups | async; trackBy: trackById" [label]="group.category.label">
                                        <mat-option
                                            *ngFor="let meas of group.data; trackBy: trackById"
                                            [value]="meas.id"
                                            (click)="$event.stopPropagation()">
                                            {{meas.label }} [{{meas.unit}}]
                                        </mat-option>
                                    </mat-optgroup>
                                </mat-select>
                                <mat-error>Select dependent measurement</mat-error>
                            </mat-form-field>
                        </div>
                    </ng-container>

                    <!--* BUBBLE -->
                    <ng-container *ngSwitchCase="'bubble'">
                        <div class="field-block">
                            <label class="field-label">Dependent measurement</label>
                            <mat-form-field appearance="outline" class="full-width">
                                <mat-select formControlName="secondaryMeasure" placeholder="Select measurement…">
                                    <mat-option>
                                        <ngx-mat-select-search
                                            [formControl]="secondaryMeasureGroupsFilterCtrl"
                                            placeholderLabel="Find Measurement..."
                                            noEntriesFoundLabel="No matching measurement found">
                                        </ngx-mat-select-search>
                                    </mat-option>
                                    <mat-optgroup *ngFor="let group of filteredSecondaryMeasureGroups | async; trackBy: trackById" [label]="group.category.label">
                                        <mat-option
                                            *ngFor="let meas of group.data; trackBy: trackById"
                                            [value]="meas.id"
                                            (click)="$event.stopPropagation()">
                                            {{meas.label }} [{{meas.unit}}]
                                        </mat-option>
                                    </mat-optgroup>
                                </mat-select>
                                <mat-error>Select dependent measurement</mat-error>
                            </mat-form-field>
                        </div>

                        <div class="field-block">
                            <label class="field-label">Radius measurement</label>
                            <mat-form-field appearance="outline" class="full-width">
                                <mat-select formControlName="tertiaryMeasure" placeholder="Select measurement…">
                                    <mat-option>
                                        <ngx-mat-select-search
                                            [formControl]="tertiaryMeasureGroupsFilterCtrl"
                                            placeholderLabel="Find Measurement..."
                                            noEntriesFoundLabel="No matching measurement found">
                                        </ngx-mat-select-search>
                                    </mat-option>
                                    <mat-optgroup *ngFor="let group of filteredTertiaryMeasureGroups | async; trackBy: trackById" [label]="group.category.label">
                                        <mat-option
                                            *ngFor="let meas of group.data; trackBy: trackById"
                                            [value]="meas.id"
                                            (click)="$event.stopPropagation()">
                                            {{meas.label }} [{{meas.unit}}]
                                        </mat-option>
                                    </mat-optgroup>
                                </mat-select>
                                <mat-error>Select Measurement to Determine Bubble Radius</mat-error>
                            </mat-form-field>
                        </div>

                        <div class="field-block">
                            <label class="field-label">Chart compilation</label>
                            <mat-form-field appearance="outline" class="full-width">
                                <mat-select formControlName="compilationType" required placeholder="Select compilation…">
                                    <mat-option *ngFor="let option of chartCompilationOptions" [value]="option.value">{{ option.label }}</mat-option>
                                </mat-select>
                                <mat-error>Select chart data compilation type</mat-error>
                            </mat-form-field>
                        </div>
                    </ng-container>

                    <!--* CANDLE STICK -->
                    <ng-container *ngSwitchCase="'boxplot'"></ng-container>

                    <!--* DEFAULT CHART TYPES -->
                    <ng-container *ngSwitchDefault>
                        <div class="two-col">
                            <div class="field-block">
                                <label class="field-label">Chart compilation</label>
                                <mat-form-field appearance="outline" class="full-width">
                                    <mat-select formControlName="compilationType" required placeholder="Select compilation…">
                                        <mat-option *ngFor="let option of chartCompilationOptions" [value]="option.value">{{ option.label }}</mat-option>
                                    </mat-select>
                                    <mat-error>Select chart data compilation type</mat-error>
                                </mat-form-field>
                            </div>

                            <div class="field-block">
                                <label class="field-label">Chart date</label>
                                <mat-form-field appearance="outline" class="full-width">
                                    <mat-select formControlName="dateFormat" required placeholder="Select interval…">
                                        <mat-option *ngFor="let option of chartDateOptions" [value]="option.value">{{ option.label }}</mat-option>
                                    </mat-select>
                                    <mat-error>Select chart date intervals</mat-error>
                                </mat-form-field>
                            </div>
                        </div>

                        <div class="field-block">
                            <label class="field-label">Chart data date</label>
                            <mat-form-field appearance="outline" class="full-width">
                                <mat-select formControlName="dataDate" required placeholder="Select date context…">
                                    <mat-option *ngFor="let option of chartDataDateOptions" [value]="option.value">{{ option.label }}</mat-option>
                                </mat-select>
                                <mat-error>Select chart data date context</mat-error>
                            </mat-form-field>
                        </div>
                    </ng-container>
                </ng-container>
            </form>
        </mat-dialog-content>

        <mat-dialog-actions align="end" class="pv-dialog-footer">
            <button type="button" mat-stroked-button (click)="cancel()">Cancel</button>
            <button type="button" mat-flat-button color="primary" (click)="attempt()">
                <mat-icon>check</mat-icon>
                Apply
            </button>
        </mat-dialog-actions>
    </ng-template>

</ng-container>
